import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  asyncTaskCancel,
  asyncTaskGet,
  asyncTaskResult,
  asyncTasksListing,
  markAsyncTaskAsSeen,
} from "@/network/network.jsx";
import AuthContext from "@/react/context/authContext.jsx";

import { FAST_POLL_MS, IDLE_POLL_MS } from "../constants/polling";
import type { AsyncTask, AsyncTaskContextValue, AsyncTaskRecordBase, RegisterConfig } from "../types";
import { parseAsyncTask, unwrapAsyncTaskResultBody } from "../utils";
import { createAsyncTaskStore } from "./asyncTaskStore";

export const AsyncTaskContext = createContext<AsyncTaskContextValue | undefined>(
  undefined,
);

export function useAsyncTaskContext(): AsyncTaskContextValue {
  const ctx = useContext(AsyncTaskContext);
  if (!ctx) {
    throw new Error("useAsyncTaskContext must be used within AsyncTaskProvider");
  }
  return ctx;
}

interface AsyncTaskProviderProps {
  children: ReactNode;
}

export function AsyncTaskProvider({ children }: AsyncTaskProviderProps) {
  const { user } = useContext(AuthContext);
  const isAuthenticated = Boolean(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  const storeRef = useRef(createAsyncTaskStore());
  const store = storeRef.current;

  const [isReady, setIsReady] = useState(false);
  const [, setRevision] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const pollStartedRef = useRef(false);
  const pollRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const resultInflightRef = useRef(new Map<number, Promise<unknown>>());
  const taskInflightRef = useRef(new Map<number, Promise<AsyncTask>>());

  const bumpRevision = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  useEffect(() => store.subscribe(bumpRevision), [store, bumpRevision]);

  const stopPolling = useCallback(() => {
    pollStartedRef.current = false;
    if (intervalRef.current != null) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;

    try {
      const res = await asyncTasksListing();
      if (!mountedRef.current || !isAuthenticatedRef.current) return;

      const rawTasks: AsyncTaskRecordBase[] = res.data ?? [];
      const parsed = rawTasks
        .map(parseAsyncTask)
        .filter((task): task is NonNullable<typeof task> => task != null);

      store.applySnapshot(parsed);

      if (!isReady) setIsReady(true);
    } catch {
      if (!isReady && mountedRef.current && isAuthenticatedRef.current) {
        setIsReady(true);
      }
    }
  }, [isReady, store]);

  pollRef.current = poll;

  const scheduleNextPoll = useCallback(() => {
    if (
      !mountedRef.current ||
      !pollStartedRef.current ||
      !isAuthenticatedRef.current
    ) {
      return;
    }

    const interval = store.hasPendingTasks() ? FAST_POLL_MS : IDLE_POLL_MS;

    if (intervalRef.current != null) clearTimeout(intervalRef.current);
    intervalRef.current = setTimeout(async () => {
      await pollRef.current();
      scheduleNextPoll();
    }, interval);
  }, [store]);

  const startPolling = useCallback(() => {
    if (!isAuthenticatedRef.current) return;
    if (pollStartedRef.current) return;
    pollStartedRef.current = true;

    void pollRef.current().then(() => {
      if (mountedRef.current && isAuthenticatedRef.current) scheduleNextPoll();
    });
  }, [scheduleNextPoll]);

  const repoll = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    await pollRef.current();
    scheduleNextPoll();
  }, [scheduleNextPoll]);

  const register = useCallback(
    (config: RegisterConfig) => {
      const cleanup = store.register(config);
      startPolling();
      return () => {
        cleanup();
      };
    },
    [store, startPolling],
  );

  const pinTask = useCallback(
    (taskId: number, hints?: Parameters<AsyncTaskContextValue["pinTask"]>[1]) => {
      if (!isAuthenticatedRef.current) return;
      store.pinTask(taskId, hints);
      startPolling();
      void pollRef.current().then(() => {
        if (mountedRef.current && isAuthenticatedRef.current) scheduleNextPoll();
      });
    },
    [store, startPolling, scheduleNextPoll],
  );

  const fetchResult = useCallback(async (taskId: number) => {
    const inflight = resultInflightRef.current;
    const existing = inflight.get(taskId);
    if (existing) return existing;

    const promise = asyncTaskResult(taskId)
      .then((res) => unwrapAsyncTaskResultBody(res.data))
      .finally(() => {
        inflight.delete(taskId);
      });

    inflight.set(taskId, promise);
    return promise;
  }, []);

  const fetchTask = useCallback(async (taskId: number) => {
    const inflight = taskInflightRef.current;
    const existing = inflight.get(taskId);
    if (existing) return existing;

    const promise = asyncTaskGet(taskId)
      .then((res) => {
        const parsed = parseAsyncTask(res.data as AsyncTaskRecordBase);
        if (!parsed) {
          throw new Error(`Failed to parse async task ${taskId}`);
        }
        return parsed;
      })
      .finally(() => {
        inflight.delete(taskId);
      });

    inflight.set(taskId, promise);
    return promise;
  }, []);

  const cancelTask = useCallback(async (taskId: number) => {
    await asyncTaskCancel(taskId);
    await repoll();
  }, [repoll]);

  const markSeen = useCallback(
    async (taskId: number, isSeen = true) => {
      await markAsyncTaskAsSeen(taskId, isSeen);
      await repoll();
    },
    [repoll],
  );

  const getTasks = useCallback(() => store.getTasks(), [store]);

  const subscribe = useCallback(
    (listener: () => void) => store.subscribe(listener),
    [store],
  );

  // Poll only while AuthContext has a user; provider stays mounted across logout.
  useEffect(() => {
    if (isAuthenticated) {
      startPolling();
    } else {
      stopPolling();
      store.clear();
      setIsReady(false);
    }
  }, [isAuthenticated, startPolling, stopPolling, store]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const value = useMemo<AsyncTaskContextValue>(
    () => ({
      isReady,
      register,
      pinTask,
      repoll,
      fetchResult,
      fetchTask,
      cancelTask,
      markSeen,
      getTasks,
      subscribe,
    }),
    [
      isReady,
      register,
      pinTask,
      repoll,
      fetchResult,
      fetchTask,
      cancelTask,
      markSeen,
      getTasks,
      subscribe,
    ],
  );

  return (
    <AsyncTaskContext.Provider value={value}>{children}</AsyncTaskContext.Provider>
  );
}
